import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../shared/components/AppLayout";
import adminTemplatesService, {
  AdminTemplate,
} from "../../services/adminTemplatesService";
import TemplateFormModal from "./components/TemplateFormModal";
import TemplateAssignModal from "./components/TemplateAssignModal";
import BuiltinSoapModal from "./components/BuiltinSoapModal";

const TemplateManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "null");

  const [templates, setTemplates] = useState<AdminTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(
    null,
  );

  const [editingTemplate, setEditingTemplate] = useState<AdminTemplate | null | "new">(
    null,
  );
  const [assigningTemplate, setAssigningTemplate] = useState<AdminTemplate | null>(null);
  const [managingBuiltinSoap, setManagingBuiltinSoap] = useState(false);


  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminTemplatesService.list();
      setTemplates(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || "Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleActive = async (t: AdminTemplate) => {
    try {
      const updated = await adminTemplatesService.update(t.id, { active: !t.active });
      setTemplates((prev) => prev.map((x) => (x.id === t.id ? updated : x)));
      showToast(updated.active ? "Template activated." : "Template deactivated.", "success");
    } catch (err: any) {
      showToast(err.response?.data?.detail || "Update failed.", "error");
    }
  };

  const handleDelete = async (t: AdminTemplate) => {
    if (
      !window.confirm(
        `Delete "${t.name}"? This cannot be undone. Doctors currently assigned to it will fall back to the default SOAP note.`,
      )
    )
      return;
    try {
      await adminTemplatesService.remove(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      showToast("Template deleted.", "success");
    } catch (err: any) {
      showToast(err.response?.data?.detail || "Delete failed.", "error");
    }
  };

  const handleSaved = (saved: AdminTemplate) => {
    setTemplates((prev) => {
      const exists = prev.some((t) => t.id === saved.id);
      return exists ? prev.map((t) => (t.id === saved.id ? saved : t)) : [saved, ...prev];
    });
    setEditingTemplate(null);
    showToast("Template saved.", "success");
  };

  return (
    <>
      <AppLayout active="admin-templates">
          <div className="max-w-5xl mx-auto">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  AI Documentation Templates
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Create structured documentation templates and assign them to
                  doctors. Doctors pick from their assigned templates when
                  generating a note.
                </p>
              </div>
              <button
                onClick={() => setEditingTemplate("new")}
                className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition shrink-0"
              >
                + New Template
              </button>
            </div>

            {toast && (
              <div
                className={`mb-4 px-4 py-3 rounded-lg text-sm font-medium ${
                  toast.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {toast.msg}
              </div>
            )}

            {loading && (
              <div className="text-center py-16 text-gray-400">Loading...</div>
            )}

            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* The table always has at least the pinned built-in SOAP row, so
                it renders even with no custom templates — otherwise the
                built-in note would be unmanageable on a fresh install. */}
            {!loading && !error && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">
                        Name
                      </th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">
                        Type
                      </th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">
                        Specialties
                      </th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">
                        Assigned Doctors
                      </th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">
                        Active
                      </th>
                      <th className="text-left px-6 py-3 font-semibold text-gray-600">
                        Updated
                      </th>
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {/* The built-in SOAP note has no DocumentTemplate row, so it
                        is pinned here to be manageable alongside the rest. It
                        cannot be edited or deleted — only shown or hidden per
                        doctor. */}
                    <tr className="bg-gray-50/60">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        <div className="whitespace-nowrap">
                          SOAP Note (Default)
                        </div>
                        <div className="text-xs text-gray-500 font-normal mt-1 leading-relaxed">
                          Available to every doctor unless disabled
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">default</td>
                      <td className="px-6 py-4 text-gray-300">—</td>
                      <td className="px-6 py-4 text-gray-300">—</td>
                      <td className="px-6 py-4">
                        <span className="inline-block whitespace-nowrap px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          Default
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-300">—</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 justify-end">
                          <button
                            onClick={() => setManagingBuiltinSoap(true)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                          >
                            Manage
                          </button>
                        </div>
                      </td>
                    </tr>
                    {templates.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-6 py-10 text-center text-gray-400"
                        >
                          No custom templates yet. Create one to get started.
                        </td>
                      </tr>
                    )}
                    {templates.map((t) => (
                      <tr key={t.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {t.name}
                          {t.description && (
                            <div className="text-xs text-gray-400 font-normal mt-0.5">
                              {t.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {t.documentation_type}
                        </td>
                        <td className="px-6 py-4 text-gray-600 text-sm">
                          {t.specialties.length > 0 ? (
                            <span title={t.specialties.join(", ")}>
                              {t.specialties.length} specialt{t.specialties.length === 1 ? "y" : "ies"}
                            </span>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-gray-600">
                          {t.assigned_doctor_ids.length}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleActive(t)}
                            className={`px-2.5 py-1 rounded-full text-xs font-semibold transition ${
                              t.active
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                          >
                            {t.active ? "Active" : "Inactive"}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-gray-500">
                          {new Date(t.updated_at).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 justify-end">
                            <button
                              onClick={() => setAssigningTemplate(t)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                            >
                              Assign
                            </button>
                            <button
                              onClick={() => setEditingTemplate(t)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(t)}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-red-300 text-red-600 hover:bg-red-50 transition"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      </AppLayout>

      {managingBuiltinSoap && (
        <BuiltinSoapModal onClose={() => setManagingBuiltinSoap(false)} />
      )}

      {editingTemplate && (
        <TemplateFormModal
          template={editingTemplate === "new" ? null : editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSaved={handleSaved}
        />
      )}

      {assigningTemplate && (
        <TemplateAssignModal
          template={assigningTemplate}
          onClose={() => {
            setAssigningTemplate(null);
            load(); // refresh assigned_doctor_ids count
          }}
        />
      )}
    </>
  );
};

export default TemplateManagementPage;
